import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import PlanLimitAlert from '../../utils/planLimitAlert';
import CustomModal from '../../hoc/customModal';
import AddConsent from './add';
import EditConsent from './edit';
import { Loader } from '../../utils/tools';
import { fetchConsents, deleteConsent, downloadConsentAttachment } from '../../store/actions/consent';
import { PLAN_LIMITS } from '../../config/plans';

// Assets
import { ReactComponent as EditIcon } from '../../assets/edit.svg';
import { ReactComponent as RemoveIcon } from '../../assets/remove.svg';

const STATUS_CONFIG = {
    active: { label: 'Platný', color: '#4C6444', bg: '#E7EEE2' },
    expiring: { label: 'Vyprší čoskoro', color: '#C97D2C', bg: '#FBEBD8' },
    expired: { label: 'Vypršal', color: '#A5432B', bg: '#F7E4DE' },
};

const FILTERS = [
    { key: 'all', label: 'Všetky' },
    { key: 'active', label: 'Platné' },
    { key: 'expiring', label: 'Vyprší čoskoro' },
    { key: 'expired', label: 'Vypršané' },
];

const MAX_BANNERS = 3;

const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString('sk-SK', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

const formatDaysUntilExpiry = (days) => {
    if (days === null || days === undefined) return '-';
    if (days < 0) return `Exspirovalo pred ${Math.abs(days)} ${Math.abs(days) === 1 ? 'dňom' : 'dňami'}`;
    if (days === 0) return 'Dnes';
    return `${days} ${days === 1 ? 'deň' : days < 5 ? 'dni' : 'dní'}`;
};

const DocumentIcon = ({ color }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8">
        <path d="M4 19V5a2 2 0 0 1 2-2h8l6 6v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
        <path d="M14 3v6h6" />
    </svg>
);

const AlertIcon = ({ color }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
        <path d="M12 9v4M12 17h.01M10.3 3.9L2.4 18a2 2 0 0 0 1.7 3h16a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    </svg>
);

const ClockIcon = ({ color }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 3" />
    </svg>
);


const Consents = () => {
    const dispatch = useDispatch();

    const consents = useSelector(state => state.consent.data);
    const loading = useSelector(state => state.consent.loading);
    const addedConsent = useSelector(state => state.consent.addedConsent);
    const editedConsent = useSelector(state => state.consent.editedConsent);
    const currentBranch = useSelector(state => state.branch.current);
    const tenant = useSelector(state => state.tenant.current);

    const planLimits = PLAN_LIMITS[tenant?.plan];
    const hasConsentLimit = planLimits?.maxConsents > 0 && planLimits?.maxConsents < 9999;

    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [consentToEdit, setConsentToEdit] = useState(null);
    const [show, setShow] = useState(false);
    const [consentIdToDelete, setConsentIdToDelete] = useState(null);
    const [consentNameToDelete, setConsentNameToDelete] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all');
    const [search, setSearch] = useState('');

    useEffect(() => {
        dispatch(fetchConsents());
    }, [dispatch, addedConsent, editedConsent]);

    const isFirstBranchRender = useRef(true);
    useEffect(() => {
        if (isFirstBranchRender.current) {
            isFirstBranchRender.current = false;
            return;
        }
        // pri zmene prevádzky v headeri sa vrátime na zoznam súhlasov namiesto zotrvania v detaile/úprave
        setIsAddModalVisible(false);
        setIsEditModalVisible(false);
        setConsentToEdit(null);
        setShow(false);
        setConsentIdToDelete(null);
        setConsentNameToDelete(null);
    }, [currentBranch]);

    const handleOpenAddModal = () => setIsAddModalVisible(true);
    const handleCloseAddModal = () => setIsAddModalVisible(false);

    const handleEditButtonClick = (consentId) => {
        setConsentToEdit(null);
        setConsentToEdit(consentId);
        setIsEditModalVisible(true);
    };

    const handleCloseEditModal = () => {
        setIsEditModalVisible(false);
        setConsentToEdit(null);
    };

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const handleRemoveButtonClick = (consentId, consentName) => {
        setConsentIdToDelete(consentId);
        setConsentNameToDelete(consentName);
        handleShow();
    };

    const handleDeleteConsent = () => {
        dispatch(deleteConsent(consentIdToDelete)).then(() => {
            dispatch(fetchConsents());
        });
        handleClose();
    };

    const handleDownload = (consent) => {
        dispatch(downloadConsentAttachment({ consentId: consent._id, filename: consent.attachment?.filename }));
    };

    const renderTooltipEdit = (props) => (
        <Tooltip id="button-tooltip" {...props}>
            Upraviť súhlas
        </Tooltip>
    );

    const renderTooltipRemove = (props) => (
        <Tooltip id="button-tooltip" {...props}>
            Vymazať súhlas
        </Tooltip>
    );

    const renderTooltipDownload = (props) => (
        <Tooltip id="button-tooltip" {...props}>
            Stiahnuť prílohu
        </Tooltip>
    );

    const list = useMemo(() => (Array.isArray(consents) ? consents : []), [consents]);

    const counts = useMemo(() => ({
        total: list.length,
        active: list.filter(c => c.status === 'active').length,
        expiring: list.filter(c => c.status === 'expiring').length,
        expired: list.filter(c => c.status === 'expired').length,
    }), [list]);

    const urgentBanners = useMemo(() => (
        [...list.filter(c => c.status === 'expired'), ...list.filter(c => c.status === 'expiring')].slice(0, MAX_BANNERS)
    ), [list]);

    const filteredConsents = useMemo(() => {
        const query = search.trim().toLowerCase();
        return list.filter((consent) => {
            if (activeFilter !== 'all' && consent.status !== activeFilter) return false;
            if (!query) return true;
            return consent.name?.toLowerCase().includes(query)
                || consent.decisionNumber?.toLowerCase().includes(query);
        });
    }, [list, activeFilter, search]);

    const isAtLimit = hasConsentLimit && list.length >= planLimits.maxConsents;

    return (
        <>
            <div className="d-flex justify-content-end mb-3">
                <Button
                    className="button-custom-large radius"
                    onClick={handleOpenAddModal}
                    disabled={isAtLimit}
                >
                    Pridať súhlas
                </Button>
            </div>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '35vh' }}>
                    <Loader />
                </div>
            ) : (
                <>
                    {hasConsentLimit && (
                        <PlanLimitAlert
                            current={list.length}
                            max={planLimits.maxConsents}
                            label="súhlasov"
                            plan={tenant?.plan}
                        />
                    )}

                    {urgentBanners.map((consent) => {
                        const isExpired = consent.status === 'expired';
                        const days = Math.abs(consent.daysUntilExpiry ?? 0);
                        const color = isExpired ? '#A5432B' : '#C97D2C';
                        const bg = isExpired ? '#F7E4DE' : '#FBEBD8';
                        const title = isExpired
                            ? `${consent.name} vypršal pred ${days} ${days === 1 ? 'dňom' : 'dňami'}`
                            : `${consent.name} vyprší o ${days} ${days === 1 ? 'deň' : days < 5 ? 'dni' : 'dní'}`;
                        const body = isExpired
                            ? 'Odporúčame čo najskôr podať žiadosť o predĺženie na vydávajúcom orgáne.'
                            : 'Ak plánujete predĺženie, odporúčame podať žiadosť s dostatočným predstihom pred vydávajúcim orgánom.';
                        return (
                            <div
                                key={consent._id}
                                style={{
                                    display: 'flex', alignItems: 'flex-start', gap: '12px',
                                    backgroundColor: bg, border: `1px solid ${color}`,
                                    borderRadius: '10px', padding: '14px 16px', marginBottom: '10px'
                                }}
                            >
                                {isExpired ? <AlertIcon color={color} /> : <ClockIcon color={color} />}
                                <div>
                                    <p style={{ fontSize: '13.5px', fontWeight: 600, color, margin: '0 0 2px' }}>{title}</p>
                                    <div style={{ fontSize: '12.5px', color: '#3D5654', lineHeight: 1.5 }}>{body}</div>
                                </div>
                            </div>
                        );
                    })}

                    <div className="d-flex flex-wrap gap-3 my-3">
                        {[
                            { label: 'Súhlasov celkom', value: counts.total, color: '#0B1157' },
                            { label: 'Platné', value: counts.active, color: '#4C6444' },
                            { label: 'Vyprší do 4 mesiacov', value: counts.expiring, color: '#C97D2C' },
                            { label: 'Vypršané', value: counts.expired, color: '#A5432B' },
                        ].map((card) => (
                            <div
                                key={card.label}
                                style={{ flex: '1 1 160px', backgroundColor: '#fff', border: '1px solid #EDF2F7', borderRadius: '10px', padding: '14px 16px' }}
                            >
                                <div style={{ fontSize: '22px', fontWeight: 700, color: card.color }}>{card.value}</div>
                                <div style={{ fontSize: '11.5px', color: '#718094', marginTop: '2px' }}>{card.label}</div>
                            </div>
                        ))}
                    </div>

                    <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                        {FILTERS.map((filter) => (
                            <button
                                key={filter.key}
                                type="button"
                                onClick={() => setActiveFilter(filter.key)}
                                style={{
                                    fontSize: '12px', padding: '6px 14px', borderRadius: '20px',
                                    border: `1px solid ${activeFilter === filter.key ? '#0B1157' : '#DDD6C8'}`,
                                    backgroundColor: activeFilter === filter.key ? '#0B1157' : '#fff',
                                    color: activeFilter === filter.key ? '#fff' : '#718094',
                                    cursor: 'pointer'
                                }}
                            >
                                {filter.label}
                            </button>
                        ))}
                        <Form.Control
                            type="text"
                            placeholder="Hľadať podľa názvu alebo čísla rozhodnutia..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{ marginLeft: 'auto', maxWidth: '260px', fontSize: '13px' }}
                        />
                    </div>

                    {filteredConsents.length === 0 ? (
                        <div className="text-center text-muted py-5">
                            <p className="mb-1"><strong>
                                {list.length === 0 ? 'Zatiaľ nemáte žiadny súhlas.' : 'Žiadny súhlas nezodpovedá filtru.'}
                            </strong></p>
                            {list.length === 0 && (
                                <p className="mb-0">Kliknutím na tlačidlo <em>Pridať súhlas</em> vytvoríte prvý súhlas.</p>
                            )}
                        </div>
                    ) : (
                        filteredConsents.map((consent) => {
                            const status = STATUS_CONFIG[consent.status] ?? STATUS_CONFIG.active;
                            const wasteCodesLabel = Array.isArray(consent.wasteCodes) && consent.wasteCodes.length
                                ? consent.wasteCodes.map(w => w.wasteCode).join(', ')
                                : null;
                            const loadingCodesLabel = Array.isArray(consent.loadingCodes) && consent.loadingCodes.length
                                ? consent.loadingCodes.join(', ')
                                : null;

                            return (
                                <div
                                    key={consent._id}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '16px',
                                        backgroundColor: '#fff', border: '1px solid #EDF2F7',
                                        borderLeft: `4px solid ${status.color}`,
                                        borderRadius: '10px', padding: '14px 18px', marginBottom: '10px'
                                    }}
                                >
                                    <div style={{
                                        width: '38px', height: '38px', borderRadius: '8px', flexShrink: 0,
                                        backgroundColor: status.bg, display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}>
                                        <DocumentIcon color={status.color} />
                                    </div>

                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#0B1157' }}>{consent.name}</div>
                                        <div style={{ fontSize: '12px', color: '#718094', marginTop: '2px' }}>{consent.consentType}</div>
                                        <div style={{ fontSize: '12px', color: '#718094', marginTop: '2px' }}>
                                            Číslo <span style={{ fontFamily: 'monospace' }}>{consent.decisionNumber}</span> · Vydal {consent.issuingAuthority}
                                        </div>
                                        {(wasteCodesLabel || loadingCodesLabel) && (
                                            <div style={{ fontSize: '11.5px', color: '#A9B0AE', marginTop: '2px' }}>
                                                {wasteCodesLabel && <>Kódy odpadov: {wasteCodesLabel}</>}
                                                {wasteCodesLabel && loadingCodesLabel && ' · '}
                                                {loadingCodesLabel && <>Kód nakladania: {loadingCodesLabel}</>}
                                            </div>
                                        )}
                                    </div>

                                    <span style={{
                                        fontSize: '11px', fontWeight: 500, padding: '4px 10px', borderRadius: '20px',
                                        whiteSpace: 'nowrap', color: status.color, backgroundColor: status.bg
                                    }}>
                                        {status.label}
                                    </span>

                                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                        <div style={{ fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#718094' }}>Platný do</div>
                                        <div style={{ fontFamily: 'monospace', fontSize: '13.5px', fontWeight: 600, color: status.color }}>
                                            {formatDate(consent.validUntil)}
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#718094' }}>
                                            {formatDaysUntilExpiry(consent.daysUntilExpiry)}
                                        </div>
                                    </div>

                                    <div className="d-flex gap-2" style={{ flexShrink: 0 }}>
                                        {consent.attachment?.filename && (
                                            <OverlayTrigger placement="top" overlay={renderTooltipDownload}>
                                                <Button
                                                    className="btnIcon"
                                                    onClick={() => handleDownload(consent)}
                                                    style={{ width: '32px', height: '32px', border: '1px solid #DDD6C8', borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                >
                                                    <DocumentIcon color="#718094" />
                                                </Button>
                                            </OverlayTrigger>
                                        )}

                                        <OverlayTrigger placement="top" overlay={renderTooltipEdit}>
                                            <Button
                                                className="btnIcon"
                                                style={{ width: '32px', height: '32px', border: '1px solid #DDD6C8', borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            >
                                                <EditIcon
                                                    onClick={() => handleEditButtonClick(consent._id)}
                                                    alt="Upraviť"
                                                    className="icon icon-edit"
                                                />
                                            </Button>
                                        </OverlayTrigger>

                                        <OverlayTrigger placement="top" overlay={renderTooltipRemove}>
                                            <Button
                                                className="btnIcon"
                                                style={{ width: '32px', height: '32px', border: '1px solid #DDD6C8', borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            >
                                                <RemoveIcon
                                                    onClick={() => handleRemoveButtonClick(consent._id, consent.name)}
                                                    alt=""
                                                    className="icon icon-remove"
                                                />
                                            </Button>
                                        </OverlayTrigger>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </>
            )}

            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Potvrdenie</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    Naozaj chcete vymazať súhlas <b>{consentNameToDelete}</b>?
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Zrušiť
                    </Button>
                    <Button variant="danger" onClick={handleDeleteConsent}>
                        Vymazať
                    </Button>
                </Modal.Footer>
            </Modal>

            <CustomModal isVisible={isAddModalVisible} onClose={handleCloseAddModal}>
                <AddConsent onClose={handleCloseAddModal} />
            </CustomModal>

            <CustomModal isVisible={isEditModalVisible} onClose={handleCloseEditModal}>
                {consentToEdit && isEditModalVisible ?
                    <EditConsent consentId={consentToEdit} onClose={handleCloseEditModal} />
                    : null
                }
            </CustomModal>
        </>
    );
};

export default Consents;
